import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../lib/apiClient";
import { CertType, Registrant } from "../lib/types";
import CopyButton from "../components/CopyButton";

type ListResponse = { registrants: Registrant[]; total: number };
type CertTypesResponse = { cert_types: CertType[]; total: number };

const STATUSES = ["complete", "expired", "missing", "awaiting_review"] as const;
const TYPES = ["statutory", "mandatory", "other"] as const;
const PAGE_SIZE = 50;

export default function RegistrantsPage() {
  const [data, setData] = useState<Registrant[] | null>(null);
  const [certTypes, setCertTypes] = useState<CertType[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Filters (server-side for registrant inclusion)
  const [statuses, setStatuses] = useState<Set<string>>(new Set());
  const [types, setTypes] = useState<Set<string>>(new Set());
  const [category, setCategory] = useState<string>("");

  // Search + pagination (client-side)
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, string> = {};
      if (statuses.size) query.status = Array.from(statuses).join(",");
      if (types.size) query.statutory_type = Array.from(types).join(",");
      if (category.trim()) query.category = category.trim();

      const resp = await apiFetch<ListResponse>("/api/v1/registrants", { query });
      setData(resp.registrants);
      setPage(1);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Request failed");
    } finally {
      setLoading(false);
    }
  }

  // Initial cert_types load to populate the category dropdown.
  useEffect(() => {
    apiFetch<CertTypesResponse>("/api/v1/cert_types")
      .then(r => setCertTypes(r.cert_types ?? []))
      .catch(() => { /* non-fatal for the list page */ });
  }, []);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const categoryOptions = useMemo(() => {
    const s = new Set<string>();
    certTypes.forEach(ct => { if (ct.category) s.add(ct.category); });
    return Array.from(s).sort();
  }, [certTypes]);

  const searched = useMemo(() => {
    if (!data) return [];
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(r =>
      r.pin.toLowerCase().includes(q) ||
      (r.first_name ?? "").toLowerCase().includes(q) ||
      (r.last_name ?? "").toLowerCase().includes(q) ||
      (r.email ?? "").toLowerCase().includes(q)
    );
  }, [data, search]);

  const totalPages = Math.max(1, Math.ceil(searched.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const paged = searched.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  function toggle(set: Set<string>, value: string): Set<string> {
    const next = new Set(set);
    if (next.has(value)) next.delete(value); else next.add(value);
    return next;
  }

  function clearFilters() {
    setStatuses(new Set());
    setTypes(new Set());
    setCategory("");
    setSearch("");
  }

  const activeFilterCount = statuses.size + types.size + (category ? 1 : 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-xl font-semibold">Registrants</h1>
        {data && (
          <span className="text-sm text-slate-500">
            {searched.length !== data.length
              ? <>{searched.length} of {data.length}</>
              : <>{data.length} total</>}
          </span>
        )}
        <button onClick={load} className="text-sm border border-slate-300 rounded px-2 py-1" disabled={loading}>
          {loading ? "Refreshing…" : "Refresh"}
        </button>
        <Link to="/registrants/new" className="text-sm bg-brand-600 text-white rounded px-3 py-1">
          + New registrant
        </Link>
        <input
          className="ml-auto border border-slate-300 rounded px-2 py-1 text-sm w-64"
          placeholder="Search PIN, name, email…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      <div className="bg-white border border-slate-200 rounded p-3 flex flex-wrap gap-4 items-start text-sm">
        <FilterChipGroup
          label="Status"
          options={STATUSES as readonly string[]}
          selected={statuses}
          onToggle={v => setStatuses(prev => toggle(prev, v))}
        />
        <FilterChipGroup
          label="Type"
          options={TYPES as readonly string[]}
          selected={types}
          onToggle={v => setTypes(prev => toggle(prev, v))}
        />
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Category</div>
          <select
            className="border border-slate-300 rounded px-2 py-1 text-sm min-w-[220px]"
            value={category}
            onChange={e => setCategory(e.target.value)}
          >
            <option value="">— any —</option>
            {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="ml-auto flex gap-2 items-center">
          <button onClick={load} className="bg-brand-600 text-white rounded px-3 py-1.5 text-sm" disabled={loading}>
            Apply
          </button>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters} className="text-xs text-slate-500 hover:text-slate-800">
              Clear ({activeFilterCount})
            </button>
          )}
        </div>
      </div>

      {error && <div className="text-red-600 bg-red-50 border border-red-200 rounded p-3 text-sm">{error}</div>}

      {data && (
        <>
          <div className="bg-white border border-slate-200 rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <Th>PIN</Th>
                  <Th>Name</Th>
                  <Th>Grade</Th>
                  <Th>Email</Th>
                  <Th>DoB</Th>
                  <Th>Approved</Th>
                  <Th>Sponsor</Th>
                  <Th>Compliance</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {paged.map(r => {
                  const completions = r.statutory_completions ?? [];
                  const bad = completions.filter(c => c.status === "missing" || c.status === "expired").length;
                  const total = completions.length;
                  return (
                    <tr key={r.pin} className="border-t border-slate-100">
                      <Td>
                        <div className="flex items-center gap-1">
                          <span className="mono">{r.pin}</span>
                          <CopyButton value={r.pin} />
                        </div>
                      </Td>
                      <Td>{(r.first_name || r.last_name) ? `${r.first_name ?? ""} ${r.last_name ?? ""}`.trim() : "—"}</Td>
                      <Td>{r.grade ?? "—"}</Td>
                      <Td className="text-slate-500">{r.email ?? "—"}</Td>
                      <Td>{r.date_of_birth ?? "—"}</Td>
                      <Td>
                        {r.approved
                          ? <span className="text-emerald-700">yes</span>
                          : <span className="text-amber-700">pending</span>}
                      </Td>
                      <Td><SponsorCell sponsored={r.sponsored} pending={r.sponsorship_pending} /></Td>
                      <Td>
                        {total === 0
                          ? <span className="text-slate-400">no cert types</span>
                          : bad === 0
                            ? <span className="text-emerald-700">{total}/{total} complete</span>
                            : <span className="text-amber-700">{total - bad}/{total} complete</span>}
                      </Td>
                      <Td>
                        <Link to={`/registrants/${r.pin}`} className="text-brand-700 hover:underline">
                          View
                        </Link>
                      </Td>
                    </tr>
                  );
                })}
                {paged.length === 0 && (
                  <tr><td colSpan={8} className="p-4 text-sm text-slate-500 text-center">No registrants match.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={pageSafe}
            totalPages={totalPages}
            totalItems={searched.length}
            pageSize={PAGE_SIZE}
            onChange={setPage}
          />
        </>
      )}
    </div>
  );
}

function FilterChipGroup({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: readonly string[];
  selected: Set<string>;
  onToggle: (v: string) => void;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className="flex flex-wrap gap-1">
        {options.map(opt => {
          const on = selected.has(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onToggle(opt)}
              className={`px-2 py-1 rounded text-xs border ${
                on
                  ? "bg-brand-600 text-white border-brand-600"
                  : "bg-white text-slate-700 border-slate-300 hover:border-brand-500"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Pagination({
  page, totalPages, totalItems, pageSize, onChange,
}: { page: number; totalPages: number; totalItems: number; pageSize: number; onChange: (p: number) => void }) {
  if (totalPages <= 1) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalItems);
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="text-slate-500">{from}–{to} of {totalItems}</span>
      <div className="ml-auto flex items-center gap-1">
        <button className="border border-slate-300 rounded px-2 py-1 disabled:text-slate-300" disabled={page === 1} onClick={() => onChange(1)}>«</button>
        <button className="border border-slate-300 rounded px-2 py-1 disabled:text-slate-300" disabled={page === 1} onClick={() => onChange(page - 1)}>‹</button>
        <span className="px-2">Page {page} / {totalPages}</span>
        <button className="border border-slate-300 rounded px-2 py-1 disabled:text-slate-300" disabled={page === totalPages} onClick={() => onChange(page + 1)}>›</button>
        <button className="border border-slate-300 rounded px-2 py-1 disabled:text-slate-300" disabled={page === totalPages} onClick={() => onChange(totalPages)}>»</button>
      </div>
    </div>
  );
}

function Th({ children }: { children?: React.ReactNode }) {
  return <th className="text-left font-medium px-3 py-2">{children}</th>;
}
function Td({ children, className = "" }: { children?: React.ReactNode; className?: string }) {
  return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>;
}

function SponsorCell({ sponsored, pending }: { sponsored: boolean; pending: boolean }) {
  if (sponsored) {
    return (
      <span className="inline-block px-1.5 py-0.5 text-[10px] rounded bg-emerald-50 text-emerald-700 border border-emerald-200"
            title="Sponsored by your organisation (licence consumed).">
        Sponsored
      </span>
    );
  }
  if (pending) {
    return (
      <span className="inline-block px-1.5 py-0.5 text-[10px] rounded bg-amber-50 text-amber-700 border border-amber-200"
            title="Sponsorship invited — awaiting registrant's response.">
        Invite pending
      </span>
    );
  }
  return <span className="text-slate-400 text-xs">—</span>;
}
